import {
  OnboardingDetailsPayload,
  OrganizationRegisterFormFields,
  UserProfileOnboarding,
} from 'src/types/registrationFormTypes';
import { DEFAULT_HANA_USE_CASE } from 'src/constants/hanaUseCases';
import { PATHS } from 'src/constants/navigateRoutes';
import { addMissingHttpProtocol } from 'src/utils/commonValidation';

const ONBOARDING_CELEBRATION_STORAGE_KEY = 'hana:onboarding:celebration';
const POST_ONBOARDING_REDIRECT_STORAGE_KEY = 'hana:onboarding:post-redirect';
const OPTIONAL_ROLE_FALLBACK = 'otherOrPreferNotToSay';
const OPTIONAL_AUTOMATION_COMFORT_FALLBACK = 'notSure';
const FIRST_OUTCOME_FALLBACK = 'justExploring';
const ONBOARDING_CELEBRATION_TARGETS = ['hero', 'trial-card'] as const;

type OnboardingCelebrationTarget =
  (typeof ONBOARDING_CELEBRATION_TARGETS)[number];

type OnboardingCelebrationState = Record<OnboardingCelebrationTarget, boolean>;

const getTrimmedOptionalString = (
  value?: string | null,
): string | undefined => {
  const trimmedValue = value?.trim();

  return trimmedValue ? trimmedValue : undefined;
};

export function getInviteUrlFromInviteToken(token: string): string {
  // Base URL for the invite links
  const baseUrl = window.location.origin;

  // Construct the full invite URL using the token
  const inviteUrl = `${baseUrl}?invitationToken=${encodeURIComponent(token)}`;

  return inviteUrl;
}

/**
 * Extracts the invitation token from an invite URL.
 * @param inviteUrl The full invite URL containing the invitationToken query parameter.
 * @returns The invitation token if present, otherwise null.
 */
export function getInviteTokenFromInviteUrl(inviteUrl: string): string | null {
  try {
    const url = new URL(inviteUrl);

    return url.searchParams.get('invitationToken');
  } catch (error) {
    // Invalid URL
    return null;
  }
}

export function getOnboardingDetailsPayload(
  userProfile: UserProfileOnboarding,
  onboardingOptions?: {
    preferredLanguage?: OrganizationRegisterFormFields['preferredLanguage'];
    primaryCalendarTimezone?: UserProfileOnboarding['primaryCalendarTimezone'];
    hanaUseCase?: UserProfileOnboarding['hanaUseCase'];
    adultConsentConfirmed?: UserProfileOnboarding['adultConsentConfirmed'];
  },
): OnboardingDetailsPayload {
  const payload: OnboardingDetailsPayload = {
    firstOutcome:
      getTrimmedOptionalString(userProfile.firstOutcome) ??
      FIRST_OUTCOME_FALLBACK,
    primaryUserRole:
      getTrimmedOptionalString(userProfile.primaryUserRole) ??
      OPTIONAL_ROLE_FALLBACK,
    automationComfort:
      getTrimmedOptionalString(userProfile.automationComfort) ??
      OPTIONAL_AUTOMATION_COMFORT_FALLBACK,
    hanaUseCase: onboardingOptions?.hanaUseCase ?? DEFAULT_HANA_USE_CASE,
    adultConsentConfirmed: onboardingOptions?.adultConsentConfirmed ?? false,
  };
  const painPoints = getTrimmedOptionalString(userProfile.painPoints);

  const memoryWebsiteUrl = getTrimmedOptionalString(
    userProfile.memoryWebsiteUrl,
  );
  const memoryChatSpaceReference = getTrimmedOptionalString(
    userProfile.memoryChatSpaceReference,
  );
  const memoryChatSpaceStartDate = getTrimmedOptionalString(
    userProfile.memoryChatSpaceStartDate,
  );
  const memoryChatSpaceEndDate = getTrimmedOptionalString(
    userProfile.memoryChatSpaceEndDate,
  );

  if (memoryWebsiteUrl) {
    payload.memoryWebsiteUrl = addMissingHttpProtocol(memoryWebsiteUrl);
  }

  if (painPoints) {
    payload.painPoints = painPoints;
  }

  if (memoryChatSpaceReference) {
    payload.memoryChatSpaceReference = memoryChatSpaceReference;
  }

  if (memoryChatSpaceStartDate) {
    payload.memoryChatSpaceStartDate = memoryChatSpaceStartDate;
  }

  if (memoryChatSpaceEndDate) {
    payload.memoryChatSpaceEndDate = memoryChatSpaceEndDate;
  }

  if (typeof userProfile.memoryChatSpaceAutoTrack === 'boolean') {
    payload.memoryChatSpaceAutoTrack = userProfile.memoryChatSpaceAutoTrack;
  }

  if (onboardingOptions?.preferredLanguage) {
    payload.preferredLanguageCode = onboardingOptions.preferredLanguage;
  }

  // Prefer the explicit override channel when a caller passes it, but fall back
  // to the user profile field so onboarding callers cannot silently drop the
  // timezone by forgetting to forward the second options object.
  const trimmedTimezone =
    getTrimmedOptionalString(onboardingOptions?.primaryCalendarTimezone) ??
    getTrimmedOptionalString(userProfile.primaryCalendarTimezone);

  if (trimmedTimezone) {
    payload.primaryCalendarTimezone = trimmedTimezone;
  }

  return payload;
}

export function insertOnboardingStepAfter(
  steps: string[],
  afterStep: string,
  insertedStep: string,
): string[] {
  if (steps.includes(insertedStep)) {
    return steps;
  }

  const insertIndex = steps.indexOf(afterStep);

  if (insertIndex === -1) {
    return [...steps, insertedStep];
  }

  return [
    ...steps.slice(0, insertIndex + 1),
    insertedStep,
    ...steps.slice(insertIndex + 1),
  ];
}

/**
 * Keeps the post-onboarding landing route in one place so all onboarding
 * entry cases transition consistently without forcing a full document reload.
 */
export function getPostOnboardingRedirectPath(): string {
  return PATHS.getStarted;
}

function persistPostOnboardingRedirectPath(path: string): void {
  window.localStorage.setItem(POST_ONBOARDING_REDIRECT_STORAGE_KEY, path);
  window.sessionStorage.setItem(POST_ONBOARDING_REDIRECT_STORAGE_KEY, path);
}

function clearPendingPostOnboardingRedirectPath(): void {
  window.localStorage.removeItem(POST_ONBOARDING_REDIRECT_STORAGE_KEY);
  window.sessionStorage.removeItem(POST_ONBOARDING_REDIRECT_STORAGE_KEY);
}

export function markPostOnboardingRedirectPending(
  path: string = getPostOnboardingRedirectPath(),
): void {
  persistPostOnboardingRedirectPath(path);
}

export function peekPendingPostOnboardingRedirectPath(): string | null {
  const pendingRedirectPath =
    window.sessionStorage.getItem(POST_ONBOARDING_REDIRECT_STORAGE_KEY) ??
    window.localStorage.getItem(POST_ONBOARDING_REDIRECT_STORAGE_KEY);

  if (!pendingRedirectPath?.startsWith('/')) {
    clearPendingPostOnboardingRedirectPath();

    return null;
  }

  return pendingRedirectPath;
}

export function consumePendingPostOnboardingRedirectPath(): string | null {
  const pendingRedirectPath = peekPendingPostOnboardingRedirectPath();

  if (!pendingRedirectPath) {
    return null;
  }

  clearPendingPostOnboardingRedirectPath();

  return pendingRedirectPath;
}

function readOnboardingCelebrationState(): OnboardingCelebrationState {
  const rawValue =
    window.localStorage.getItem(ONBOARDING_CELEBRATION_STORAGE_KEY) ??
    window.sessionStorage.getItem(ONBOARDING_CELEBRATION_STORAGE_KEY);

  if (!rawValue) {
    return {
      hero: false,
      'trial-card': false,
    };
  }

  if (rawValue === 'true') {
    return {
      hero: true,
      'trial-card': true,
    };
  }

  try {
    const parsedValue = JSON.parse(
      rawValue,
    ) as Partial<OnboardingCelebrationState>;

    return {
      hero: Boolean(parsedValue.hero),
      'trial-card': Boolean(parsedValue['trial-card']),
    };
  } catch (_error) {
    return {
      hero: false,
      'trial-card': false,
    };
  }
}

export function markOnboardingCelebrationPending(): void {
  const serializedValue = JSON.stringify({
    hero: true,
    'trial-card': true,
  } satisfies OnboardingCelebrationState);

  window.localStorage.setItem(
    ONBOARDING_CELEBRATION_STORAGE_KEY,
    serializedValue,
  );
  window.sessionStorage.setItem(
    ONBOARDING_CELEBRATION_STORAGE_KEY,
    serializedValue,
  );
}

export function hasOnboardingCelebrationPending(
  target: OnboardingCelebrationTarget = 'hero',
): boolean {
  return readOnboardingCelebrationState()[target];
}

export function consumeOnboardingCelebrationPending(
  target: OnboardingCelebrationTarget = 'hero',
): boolean {
  const currentState = readOnboardingCelebrationState();
  const isPending = currentState[target];

  if (isPending) {
    const nextState = {
      ...currentState,
      [target]: false,
    };
    const hasPendingTarget = ONBOARDING_CELEBRATION_TARGETS.some(
      (candidateTarget) => nextState[candidateTarget],
    );

    if (hasPendingTarget) {
      const serializedValue = JSON.stringify(nextState);

      window.localStorage.setItem(
        ONBOARDING_CELEBRATION_STORAGE_KEY,
        serializedValue,
      );
      window.sessionStorage.setItem(
        ONBOARDING_CELEBRATION_STORAGE_KEY,
        serializedValue,
      );
    } else {
      window.localStorage.removeItem(ONBOARDING_CELEBRATION_STORAGE_KEY);
      window.sessionStorage.removeItem(ONBOARDING_CELEBRATION_STORAGE_KEY);
    }
  }

  return isPending;
}
